<template>
    <div>

        <QuickAdd v-if="$page.props.auth.user.role == 'mtd'" :isWholesale="isWholesale" :update="update" />

        <card id="checkoutTable" icon="fas fa-shopping-cart"
            :title="`${shippedCartItems.length} ${shippedCartItems.length > 1 ? 'items' : 'item'} ready to be shipped`"
            bodyClasses="p-0">
            <div class="px-3 pt-2">
                <template v-for="(item, itemIndex) in shippedCartItems" :key="item.product.id">

                    <ProductRow :item="item" :sku="item.product.sku"
                        :margin="`${itemIndex + 1 === shippedCartItems.length ? 'mb-0' : 'mb-3'}`">
                        <template v-slot:headerContent>
                            <div class="col-4">
                                <p class="pt-1 badge badge-success ml-2 mt-2 float-right" v-if="item.from_inventory">Item is
                                    on Truck</p>
                                <p class="pt-1 badge badge-primary ml-2 mt-2 float-right" v-else>Item Will Ship</p>
                                <div v-if="!isWholesale && $page.props.auth.user.role == 'mtd' && !item.product.user_id">
                                    <div @click="changeToInventory(item)"
                                        class="badge badge-secondary pt-1 ml-2 mt-2 mb-2 float-right cursor-pointer"
                                        v-if="!item.from_inventory"><i class="fas fa-truck mr-1 fa-sm"></i>Change to From
                                        Truck</div>
                                    <div @click="changeToShipping(item)"
                                        class="badge badge-secondary pt-1 ml-2 mt-2 float-right cursor-pointer" v-else><i
                                            class="fas fa-box mr-1 fa-sm"></i>Change to Shipping</div>
                                </div>
                            </div>
                        </template>

                        <div class="d-flex flex-column flex-lg-row">
                            <div v-if="!isPromotion">
                                <PriceDisplay :product="item.product" :showEditPrice="userCanEditPrice(isWholesale)"
                                    :itemIndex="itemIndex" :cartItem="item" :setItemDiscountRoute="setItemDiscountRoute"
                                    :clearItemDiscountRoute="clearItemDiscountRoute" @onUpdate="update" />
                            </div>
                        </div>

                        <div class="qty mt-2 font-weight-normal">
                            <Quantity :item="item" @quantityChanged="quantityChanged" @deleteItem="deleteItem"
                                @increment="increaseQuantity" @decrement="decreaseQuantity" />
                        </div>

                        <template v-slot:priceInfo v-if="!isPromotion">
                            <h5 class="m-0 text-success text-left text-lg-right">
                                ${{ formatCurrency(item.product.productPrice.price * item.qty) }}</h5>
                            <p v-if="item.ship_flat_cost > 0" class="text-secondary pt-1 mt-1 text-size-small border-top">
                                ${{ formatCurrency((item.ship_flat_cost * item.qty)) }} Item Flat Shipping</p>
                        </template>
                    </ProductRow>
                </template>
            </div>
        </card>
        <div class="m-3" v-if="!isPromotion && this.$page.props.cartTotals.subtotal">
            <p class="mb-0 text-right"><strong>Subtotal:</strong>
                ${{ formatCurrency(this.$page.props.cartTotals.subtotal) }}</p>
        </div>

        <card v-if="backorderCartItems.length" title="These Items will be Placed on Backorder" class="mt-3">
            <template v-for="(item, itemIndex) in backorderCartItems" :key="item.id">
                <ProductRow :item="item" :sku="item.product.sku"
                    :margin="`${itemIndex + 1 === backorderCartItems.length ? 'mb-0 pb-0 border-bottom-0' : 'mb-3'}`">
                    <template v-slot:headerContent>
                        <div class="col-4">
                            <p class="badge badge-primary pt-1 ml-2 float-right">
                                <i class="fas fa-clock mr-1"></i>
                                Expected to Ship: {{ getBackorderEta(item) }}
                            </p>
                        </div>
                    </template>

                    <div class="d-flex flex-column flex-lg-row">
                        <div>
                            <PriceDisplay :product="item.product" :showEditPrice="userCanEditPrice(isWholesale)"
                                :itemIndex="itemIndex" :cartItem="item" :setItemDiscountRoute="setItemDiscountRoute"
                                :clearItemDiscountRoute="clearItemDiscountRoute" @onUpdate="update" />
                        </div>
                    </div>

                    <div class="qty mt-2 font-weight-normal">
                        <Quantity :item="item" @quantityChanged="quantityChanged" @deleteItem="deleteItem"
                            @increment="increaseQuantity" @decrement="decreaseQuantity" />
                    </div>

                    <template v-slot:priceInfo>
                        <h5 class="m-0 text-success text-left text-lg-right"
                            :class="{ 'on-sale text-danger': item.product.productPrice.isDiscounted }">
                            ${{ formatCurrency(item.product.productPrice.price * item.qty) }}</h5>
                        <p v-if="item.ship_flat_cost > 0" class="text-secondary pt-1 mt-1 text-size-small border-top">
                            ${{ formatCurrency((item.ship_flat_cost * item.qty)) }} Item Flat Shipping</p>
                    </template>
                </ProductRow>
            </template>
        </card>
        <div class="m-3" v-if="!isPromotion && this.$page.props.cartTotals.backorderSubtotal">
            <p class="mb-0 text-right"><strong>Backorder Subtotal:</strong>
                ${{ formatCurrency(this.$page.props.cartTotals.backorderSubtotal) }}</p>
        </div>

        <card v-if="hasFutureItems" title="These items will be placed on a future order" class="mt-3">
            <template v-for="(item, itemIndex) in withFutureCartItems" :key="item.id">
                <ProductRow :item="item" :sku="item.product.sku"
                    :margin="`${itemIndex + 1 === withFutureCartItems.length ? 'mb-0 pb-0 border-bottom-0' : 'mb-3'}`"
                    v-if="item.future_items.length">
                    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center checkout-future-item"
                        v-for="futureItem in item.future_items">
                        <div>
                            <div class="d-flex flex-row justify-content-between align-items-center">
                                <Calendar class="w-100 form-control-sm px-0 mr-1 h-100" disabled
                                    style="min-width: 120px;max-width: 125px;" inputClass="w-100 small"
                                    dateFormat="mm/dd/yy" v-model="futureItem.order_future_date" />
                                <div class="qty font-weight-normal">
                                    <Quantity :item="Object.assign({}, item, futureItem, { id: item.id })"
                                        @quantityChanged="quantityChanged" @deleteItem="deleteItem"
                                        @increment="increaseQuantity" @decrement="decreaseQuantity" />
                                </div>
                            </div>
                        </div>
                    </div>
                </ProductRow>
            </template>
        </card>
        <div class="m-3" v-if="!isPromotion && this.$page.props.cartTotals.futureSubtotal">
            <p class="mb-0 text-right"><strong>Future Subtotal:</strong>
                ${{ formatCurrency(this.$page.props.cartTotals.futureSubtotal) }}</p>
        </div>
    </div>
</template> 

<script>
import Quantity from '@/Shared/Quantity'
import ProductRow from '@/Components/Shop/ProductRow'
import QuickAdd from '@/Components/Shop/QuickAdd'
import Calendar from 'primevue/calendar'
import PriceDisplay from '@/Components/Shop/PriceDisplay'
export default {
    data() {
        return {
            mtd_disable_tax: false,
            formObject: this.$inertia.form({
                item_id: null,
                qty: null,
                order_future_date: null,
                cart_item_future_id: null,
            }),
            formDeleteItem: this.$inertia.form({
                order_future_date: null,
                item_id: null
            }),
            formUpdateDisableTax: this.$inertia.form({
                mtd_disable_tax: this.mtd_disable_tax
            }),
            formUpdateProduct: this.$inertia.form({
                item_id: null
            }),
        }
    },
    components: {
        ProductRow,
        Quantity,
        QuickAdd,
        Calendar,
        PriceDisplay,
    },
    props: {
        items: Array,
        lineItems: Array,
        isWholesale: {
            type: Boolean,
            default: false
        },
        updateItemRoute: String,
        deleteItemRoute: String,
        toggleTaxDisabledRoute: String,
        setItemDiscountRoute: String,
        clearItemDiscountRoute: String,
        isPromotion: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        hasFutureItems() {
            return this.items.some(item => item.future_items.length > 0);
        },
        getUpdateItemRoute() {
            if (this.updateItemRoute) return this.updateItemRoute;
            return this.route(this.isWholesale ? 'mtd.cart.updateItem' : 'cart.updateItem')
        },
        shippedCartItems() {
            return this.items.filter(item => {
                const fromTruck = !this.isWholesale && item.from_inventory
                return (!item.show_as_backorder || fromTruck) && item.qty > 0
            })
        },
        backorderCartItems() {
            return this.items.filter(item => {
                const fromTruck = !this.isWholesale && !item.from_inventory

                return (this.isWholesale ? item.show_as_backorder : item.show_as_backorder && fromTruck) && item.qty > 0
            })
        },
        withFutureCartItems() {
            this.items.forEach(item => {
                item.future_items.forEach(futureItem => {
                    futureItem.order_future_date = futureItem.order_future_date ? new Date(futureItem.order_future_date) : futureItem.order_future_date
                })
            })
            return this.items
        },
        getDeleteItemRoute() {
            if (this.deleteItemRoute) return this.deleteItemRoute;
            return this.route(this.isWholesale ? 'mtd.cart.deleteItem' : 'cart.deleteItem');
        },
        getToggleTaxDisabledRoute() {
            if (this.toggleTaxDisabledRoute) return this.toggleTaxDisabledRoute;
            return this.route('mtd.cart.settaxdisabled');
        }
    },
    methods: {
        saveItem(item) {
            this.formObject.order_future_date = item.order_future_date
            this.formObject.cart_item_future_id = item.cart_item_future_id
            this.formObject.item_id = item.id
            this.formObject.qty = item.qty

            this.formObject.post(this.getUpdateItemRoute, {
                preserveScroll: true,
                onSuccess: () => {
                    this.update();
                    this.trackCartQuantityUpdate({
                        id: item.product.id,
                        name: item.product.name,
                        price: item.price,
                    }, item.linked_qty, item.qty, this.$page.props);
                },
            })
        },
        deleteItem(item) {
            if (this.formDeleteItem.processing) return;
            this.formDeleteItem.order_future_date = item.order_future_date
            this.formDeleteItem.item_id = item.id
            this.formDeleteItem.post(this.getDeleteItemRoute, {
                preserveScroll: true,
                onSuccess: () => {
                    this.update();
                    this.trackRemoveFromCart({
                        id: item.product.id,
                        name: item.product.name,
                        price: item.price,
                    }, item.qty, this.$page.props);
                },
            })
        },
        quantityChanged($event, item) {
            item.qty = $event.target.value
            this.saveItem(item)
        },
        increaseQuantity(item) {
            item.qty++
            this.saveItem(item)
        },
        decreaseQuantity(item) {
            item.qty--
            this.saveItem(item)
        },
        update() {
            this.$emit('onUpdate')
        },
        disableTaxChanged(e) {
            this.formUpdateDisableTax.mtd_disable_tax = e.target.checked ? 1 : 0;
            this.formUpdateDisableTax.post(this.getToggleTaxDisabledRoute, {
                preserveScroll: true,
                onSuccess: () => this.update()
            })
        },
        getBackorderEta(item) {
            if (item.backorder_eta % 7 === 0)
                return item.backorder_eta / 7 + ' weeks'
            else
                return item.backorder_eta + ' days'
        },
        changeToInventory(item) {
            if (item.from_inventory || this.formUpdateProduct.processing) return;

            this.formUpdateProduct.item_id = item.id;
            this.formUpdateProduct.post(this.route('mtd.cart.changeToInventory'), {
                preserveScroll: true,
                onSuccess: () => this.update()
            })
        },
        changeToShipping(item) {
            if (!item.from_inventory || this.formUpdateProduct.processing) return;

            this.formUpdateProduct.item_id = item.id;
            this.formUpdateProduct.post(this.route('mtd.cart.changeToShipping'), {
                preserveScroll: true,
                onSuccess: () => this.update()
            })
        },
    }
}
</script>

<style scoped>
.total-text {
    font-size: 1.15rem;
}
</style>

<style lang="scss">
.checkout-future-item .p-calendar .p-datepicker-trigger {
    height: 25px;
}

.checkout-future-item .p-calendar .p-inputtext {
    height: 25px;
}
</style>
